import { $ui, CONFIRM_REJECT_CANCEL, dialogConfirm, loadingHandler, promiseConfirm, toast } from '@/utils/ui'
import {
  BREAK_FINISHED,
  BREAK_IN_PROGRESS,
  REPEAT_HEBDOMADAIRE,
  REPEAT_HOLIDAYS,
  REPEAT_MENSUELLE,
  REPEAT_QUOTIDIENNE,
  SERVICE_ACTIVITY_TYPE_ID_INTERVENTION,
  SERVICE_CIRCUIT_COMMON_IDENTITY,
  SERVICE_COMMON_IDENTITY,
  SERVICE_EVENTS,
  SERVICES_ERROR_CHECK_FORBIDDEN,
  SERVICES_ERRORS_KEYED,
  SERVICES_STATUS_ACTIFS,
  STATUS_FINISHED,
  STATUS_IN_PROGRESS,
  STATUS_UNPROGRAMMED
} from '@/consts/services'
import store from '@/store'
import { SERVICE_BREAK_POST } from '@/store/modules/service/breaks/action-types'
import { SERVICE_POST, SERVICE_PUT_CIRCUIT_SERVICE } from '@/store/modules/service/action-types'
import router from '@/router'
import { date, Dialog } from 'quasar'
import {
  dateFormatFr,
  dateFormatIso,
  dateTimeFormatFr,
  dateTimeFormatIso,
  minutesFormatHm,
  parseIso,
  timeFormat,
  timeFormatLong
} from '@/utils/data/date'
import { modalInterventionReport, modalServiceEnd } from '@/utils/ui/modal'
import DialogMotifFinDeService from 'components/Dialog/DialogMotifFinDeService'
import { getRealHoliday } from '@/utils/index'
import {
  ROUTE_NAME_TERRAIN_CIRCUIT_ACTIF,
  ROUTE_NAME_TERRAIN_PLANNING,
  ROUTE_NAME_TERRAIN_SERVICE_ACTIF
} from '@/router/terrain'
import { checkCircuits } from '@/utils/api/agency/circuits'
import { fetchServices, multiActionsService } from '@/utils/api/services'
import { userName } from '@/utils/data/agents'
import { MODAL_EVENT_UPDATE_SERVICES } from '@/consts/app'

/**
 * Bascule automatiquement la pause
 */
export const basculerPause = () => {
  const enPause = store.getters['service/isPaused']
  const company = store.getters['user/company']
  dialogConfirm({
    message: enPause
      ? 'Mettre fin à la pause ?'
      : 'Démarrer une pause ?'
  }).onOk(() => {
    const b = {
      status: enPause
        ? BREAK_FINISHED
        : BREAK_IN_PROGRESS,
      notWork: company.breaksNotWork,
      notCharge: company.breaksNotCharge
    }
    return loadingHandler(() => store.dispatch('service/' + SERVICE_BREAK_POST, b))
  })
}

/**
 * Vérifie si il y a des erreurs dans les circuits des services
 * @param {Array} services
 */
export const checkServicesFormError = async (services = []) => {
  const errorInServiceCircuitForm = services.some((el) => el.services.some((service) => '_error' in service && service._error !== true))
  if (!errorInServiceCircuitForm) {
    return
  }
  await promiseConfirm({
    title: 'Confirmation',
    message: 'Votre tournée contient des services en chevauchement horaire, confirmer la création ?'
  })
}

/**
 * Effectue les vérifications nécessaires lors de la validation d'un ou plusieurs services
 * @param {Array} services
 * @returns {Promise<>}
 */
export const checkServices = async (services = []) => {
  if (services.length) {
    const servicesWithErrors = await loadingHandler(async () => {
      const { services: _servicesWithErrors } = await checkCircuits({ services: services })
      return _servicesWithErrors
    }, { message: 'Validation...', reject: true })
    if (servicesWithErrors.some((s) => s.errors.length)) {
      const agencySettingsFeature = store.getters['user/company'].agencySettingsFeature || {}
      const mappedErrors = servicesWithErrors.map((s) => {
        if (s.errors.length) {
          const serviceTitle = `Service du ${dateTimeFormatFr(s.startAt)}`
          return `<div class="scroll">${serviceTitle}<ul>
          ${s.errors.map((e) => {
            const serviceError = SERVICES_ERRORS_KEYED[e.type] || {}
            if (agencySettingsFeature[serviceError.agencySettingsFeatureKey] === SERVICES_ERROR_CHECK_FORBIDDEN) {
              toast.negative([serviceTitle, e.message].join(' : '))
              throw CONFIRM_REJECT_CANCEL
            }
            return `<li>${e.message}</li>`
          }).join('')}
          </ul></div>`
        }
        return ''
      })
      await promiseConfirm({
        title: 'Les erreurs suivantes ont été détectées',
        message: mappedErrors.join(''),
        html: true
      })
    }
    return true
  }
}

/**
 * Cherche les services en conflit avec une indisponibilité et propose la désaffectation de l'agent
 * @param unavailability
 * @return {Promise<void>}
 */
export const checkServicesConflictingUnavailability = async (unavailability = {}) => {
  const start = unavailability.startAt
  const end = unavailability.endAt
  const agent = unavailability.agent
  const { data } = await loadingHandler(async => fetchServices({
    filters: {
      start,
      end,
      agents: [agent.slug],
      status: SERVICES_STATUS_ACTIFS.map((s) => s.slug)
    },
    limit: 0
  }), { message: 'Recherche des conflits', reject: true })
  if (!data.length) {
    return
  }
  const message = data.map((s) =>
    `<li>Le ${dateFormatFr(s.startAt)} ${timeFormat(s.startAt)} ${s.endAt ? `- ${timeFormat(s.endAt)}` : ''} ${s.site ? `<br/>${s.site.name}` : ''}
     ${s.specialty ? `<br/>${s.specialty.name}` : ''} ${s.services && s.services.length > 0 ? '<br/>Tournée' : ''}</li>`
  )
  await dialogConfirm({
    title: `Souhaitez vous désaffecter l'agent "${userName(agent)}" des services suivants ?`,
    message: `<ul class="scroll" style="max-height: 60vh;">${message.join('')}</ul>`,
    cancel: 'Non',
    ok: 'Oui',
    html: true
  }).onOk(() => loadingHandler(async () => {
    await multiActionsService(
      'remove-agents',
      { services: data.map((s) => s.reference) }
    )
    $ui.$emit(MODAL_EVENT_UPDATE_SERVICES, data.map((s) => ({ ...s, agent: null })))
  }))
}

/**
 * Affiche le bon d'intervention du service
 * @param service
 * @param {Boolean} exit - Terminer le service après rédaction
 * @returns {Promise<void>}
 */
export const showInterventionReport = async (service, exit = false) => {
  const modal = await modalInterventionReport(service.interventionReport)
  modal.on('submit', (interventionReport) => {
    loadingHandler(async () => {
      const action = store.getters['service/isCircuit']
        ? SERVICE_PUT_CIRCUIT_SERVICE // Intervention dans une tournée
        : SERVICE_POST // Intervention pure
      await store.dispatch('service/' + action,
        {
          id: service.id,
          interventionReport
        })
      modal.hide()
      if (exit) {
        basculerService(store.getters['service/serviceActif'])
      }
    })
  })
}

/**
 * Convertie les checkpoints d'un site en serviceCheckpoint
 */
export const computeServiceCheckpointsFromSite = (checkpoints = []) =>
  checkpoints.filter((c) => c.startPoint || c.endPoint)
    .map(
      ({ id, ...c }) => ({
        ...c,
        checkpoint: { id }
      })
    )

/**
 * Affiche une demande de motif pour la fin de service
 * @returns {Promise<unknown>}
 * @private
 */
const _promptMotifFinDeService = () => new Promise((resolve, reject) => {
  Dialog.create({
    component: DialogMotifFinDeService
  })
    .onOk(resolve)
    .onCancel(() => reject(new Error(`Annulation de la fin de service`)))
})

/**
 * Permet de basculer l'état d'un service
 * TODO trop compliqué, à réécrire
 * @param checkpoint
 * @param service
 * @returns {*}
 */
export const basculerService = async ({ checkpoint, ...service }) => {
  const rondes = store.getters['service/rondes']
  const allRoundsFinished = rondes.every(ronde => {
    return (ronde.serviceRoundExecutions && ronde.serviceRoundExecutions.length >= ronde.numberOfExecutions)
  })
  if (!allRoundsFinished) {
    try {
      await promiseConfirm({
        message: 'Il faut terminer toutes les rondes avant de terminer le service, terminer le service quand même ?'
      })
    } catch (e) {
      return
    }
  }
  const { status, id } = service
  if (status === STATUS_FINISHED && (!service.endAt || service.endAt < dateTimeFormatIso(new Date()))) {
    return toast.negative('Le service est terminé')
  }
  const circuitOuServiceActif = store.state.service.active
  const serviceActif = store.getters['service/serviceActif']
  const isCircuit = store.getters['service/isCircuit']
  const targetIsCircuitOuServiceActif = circuitOuServiceActif && circuitOuServiceActif.id === id
  const isSite = isCircuit && !targetIsCircuitOuServiceActif
  const action = isSite
    ? SERVICE_PUT_CIRCUIT_SERVICE
    : SERVICE_POST
  const hasStart = status >= STATUS_IN_PROGRESS
  if (
    hasStart && // L'agent peut terminer un service non terminé
    (
      targetIsCircuitOuServiceActif ||
      (serviceActif && serviceActif.id === id)
    )) {
    if (isIntervention(service) &&
      !_.get(service, ['interventionReport', 'signature'])) {
      toast.info(`La signature d'un bon d'intervention est obligatoire`)
      return showInterventionReport(service, true)
    }
    if (service.endPoint && !checkpoint) {
      return modalServiceEnd(service)
    } else {
      await promiseConfirm({ message: `Êtes vous sur de vouloir ${isSite ? 'quitter le site' : 'terminer le service'} ?` })
      const detail = service.endAt && parseIso(service.endAt) > new Date()
        ? await _promptMotifFinDeService()
        : null
      return loadingHandler(
        async () => {
          await store.dispatch(
            'service/' + action,
            {
              ...service,
              status: STATUS_FINISHED,
              checkpoint,
              detail
            })
          try {
            await router.replace({
              name: isSite
                ? ROUTE_NAME_TERRAIN_CIRCUIT_ACTIF
                : ROUTE_NAME_TERRAIN_PLANNING
            })
          } catch (e) {}
        }, { reject: true })
    }
  }
  if (serviceActif) {
    return toast.negative('Un service est actif')
  }
  await promiseConfirm({
    message: hasStart
      ? 'Le service est déjà commencé. Acter une reprise de service ?'
      : `Êtes-vous sûr de vouloir ${isCircuit ? `confirmer l'arrivée sur site` : `commencer ${service.isCircuit ? 'cette tournée' : 'ce service'}`} ?`
  })
  return loadingHandler(async () => {
    await store.dispatch(
      'service/' + action,
      {
        ...service,
        checkpoint,
        status: STATUS_IN_PROGRESS
      })
    return router.replace({
      name: service.isCircuit
        ? ROUTE_NAME_TERRAIN_CIRCUIT_ACTIF
        : ROUTE_NAME_TERRAIN_SERVICE_ACTIF
    })
  }, { reject: true })
}

/**
 * Récupère la liste complète des évents, y compris ceux des services enfant
 * @param service
 */
export const getAllEvents = (service) => {
  const events = [...(service[SERVICE_EVENTS] || [])]
  ;(service.services || []).forEach(
    (s) => events.push(
      ...(s[SERVICE_EVENTS] || []).map(
        (s) => ({
          ...s,
          _inset: true
        })
      )
    )
  )
  return _.sortBy(events, 'createdAt').reverse()
}

/**
 * Génère une slug capable de déterminer une divergeance entre plusieurs services
 * @param service
 */
export const fullSlug = (service = {}) => (
  service.isCircuit
    ? SERVICE_CIRCUIT_COMMON_IDENTITY
    : SERVICE_COMMON_IDENTITY).map((s) => _.get(service, s)).join('-')

/**
 * Génère le slug du service
 * @param s
 * @returns {string}
 */
export const getTimeSlug = (s) => {
  const hStart = s.startAtTime ? s.startAtTime.split(':').join('h') : timeFormatLong(s.startAt)
  const hEnd = s.endAtTime ? s.endAtTime.split(':').join('h') : timeFormatLong(s.endAt)
  return hStart + '-' + hEnd
}

export const isServiceUnprogrammed = (service) => {
  return service.status === STATUS_UNPROGRAMMED
}

export const isServiceUnvalidate = (service) => {
  return service.status === STATUS_FINISHED
}

export const isIntervention = ({ activityType } = {}) => {
  return activityType && activityType.id === SERVICE_ACTIVITY_TYPE_ID_INTERVENTION
}

/**
 * Créé une copie d'un service en altérant ses propriétés en fonction des options fournies
 * @param service
 * @param startAt
 * @param minutes
 * @returns {{}}
 */
export const cloneService = (service, { startAt, minutes } = {}) => {
  if (!(startAt && minutes)) {
    throw new Error('Can not clone without startAt date or minutes')
  }
  const _service = _.cloneDeep(service)
  _service.startAt = dateTimeFormatIso(startAt)
  _service.endAt = dateTimeFormatIso(date.addToDate(startAt, { minutes }))
  if (Array.isArray(_service.services)) {
    _service.services = _service.services.map(({ ...s }) => {
      const _startAtDate = _service.startAtTime > s.startAtTime
        ? date.addToDate(startAt, { days: 1 })
        : startAt
      s.startAt = [dateFormatIso(_startAtDate), s.startAtTime].join('T') + ':00'

      const _endAtDate = s.startAtTime > s.endAtTime
        ? date.addToDate(startAt, { days: 1 })
        : startAt
      s.endAt = [dateFormatIso(_endAtDate), s.endAtTime].join('T') + ':00'

      return s
    })
  }
  return _service
}

/**
 * Génère une série de services ainsi que les informations sur la récurrence
 * @param {string} startAtDate - Date de début
 * @param {string} endAtDate - Date de fin
 * @param {string} startAtTime - Heure de début du service
 * @param {string} endAtTime - Heure de fin du service
 * @param {Object} service - Le service à répliquer
 * @param {{value: string}} repeat - Option de répétition (cf: REPEAT_OPTIONS)
 * @param {Number} frequency - Fréquence de la récurence
 * @param {Object} days - Objet contenant une association clé valeurs des jours hebdomadaire à exploiter
 * @param {Object} monthDays - Object contenant une association clé valeur des jours du mois à exploiter
 * @param {Object} holidayDays - Object contenant une association clé valeur des jours fériés à exploiter
 * @param {Boolean} excludeHolidays - Exclure les jours fériés
 * @returns {{services: []}}
 */
export const getServicesFromRecurrence = ({ startAtDate, endAtDate, startAtTime, endAtTime, ...service }, {
  repeat,
  frequency = 1,
  days = [],
  monthDays = {},
  holidayDays = {},
  excludeHolidays = false
} = {}) => {
  if (!(startAtDate && endAtDate && startAtTime && endAtTime && repeat)) {
    throw new Error('Missing parameters')
  }
  const services = []
  const holidays = []
  const dStart = parseIso([startAtDate, startAtTime].join('T'))
  const dEnd = parseIso([endAtDate, endAtTime].join('T'))
  const startTime = dStart.getTime()
  const endTime = dEnd.getTime()
  if (!(startTime && endTime && startTime < endTime)) {
    throw new Error('Wrong parameters')
  }
  let currentStartAt = new Date(startTime)
  const minutes = date.getDateDiff(
    startAtTime >= endAtTime
      ? date.addToDate(parseIso([startAtDate, endAtTime].join('T')), { days: 1 })
      : parseIso([startAtDate, endAtTime].join('T')),
    currentStartAt,
    'minutes'
  )
  const holidaysInstance = store.getters['user/holidaysInstance']

  /**
   * Ajoute une date fériée dans holidays et génère la clé de date
   * @param holiday
   * @returns {{value}}
   */
  const addHoliday = (holiday) => {
    const value = holiday.date.split(' ')[0]
    const h = {
      ...holiday,
      value
    }
    holidays.push(h)
    return h
  }

  /**
   * Ajoute le service en prenant en compte le paramètre exlude holiday
   * @param startAt
   * @param opt
   */
  const addServiceClone = ({ startAt, ...opt }) => {
    if (excludeHolidays) {
      const h = getRealHoliday(holidaysInstance.isHoliday(startAt))
      if (h) {
        addHoliday(h)
        if (!holidayDays[dateFormatIso(startAt)]) {
          return
        }
      }
    }
    services.push(cloneService(service, { startAt, ...opt }))
  }

  switch (repeat.value) {
    case REPEAT_QUOTIDIENNE: {
      const offset = { days: Math.max(frequency, 1) }
      for (; currentStartAt.getTime() < endTime; currentStartAt = date.addToDate(currentStartAt, offset)) {
        addServiceClone({ startAt: currentStartAt, minutes })
      }
      break
    }
    case REPEAT_HEBDOMADAIRE: {
      const lastWeekDay = +Object.keys(days).filter((k) => days[k]).reduce((a, b, c) => a > b || +a === 0 ? a : b)
      const skipWeek = Math.max(0, frequency - 1)
      for (; currentStartAt.getTime() < endTime; currentStartAt = date.addToDate(currentStartAt, { days: 1 })) {
        const day = currentStartAt.getDay()
        if (days[day]) {
          addServiceClone({ startAt: currentStartAt, minutes })
        }
        if (skipWeek && lastWeekDay === day) {
          currentStartAt = date.addToDate(currentStartAt, { days: 7 * skipWeek })
        }
      }
      break
    }
    case REPEAT_MENSUELLE: {
      for (; currentStartAt.getTime() < endTime; currentStartAt = date.addToDate(currentStartAt, { month: Math.max(frequency, 1) })) {
        _.mapValues(monthDays, (v, d) => {
          if (!v) {
            return
          }
          const sStartAt = date.adjustDate(currentStartAt, { date: d })
          if (sStartAt.getDate() === +d) {
            addServiceClone({ startAt: sStartAt, minutes })
          }
        })
      }
      break
    }
    case REPEAT_HOLIDAYS: {
      const endYear = dEnd.getFullYear()
      const ajust = {
        hours: dStart.getHours(),
        minutes: dStart.getMinutes()
      }
      for (let i = dStart.getFullYear(); i <= endYear; i++) {
        holidaysInstance.getHolidays(i)
          .forEach((h) => {
            const realHoliday = getRealHoliday(h)
            if (realHoliday && h.start.getTime() < endTime && startTime < h.end.getTime()) {
              const { value } = addHoliday(h)
              if (holidayDays[value]) {
                const startAt = date.adjustDate(
                  h.start,
                  ajust)
                services.push(cloneService(service, { startAt, minutes }))
              }
            }
          })
      }
    }
  }
  const firstStartAt = services.length
    ? dateTimeFormatFr(_.head(services).startAt)
    : ''
  const lastEndAt = services.length
    ? dateTimeFormatFr(_.last(services).endAt)
    : ''
  return {
    startAt: dateTimeFormatFr(dStart),
    endAt: dateTimeFormatFr(dEnd),
    firstStartAt,
    lastEndAt,
    holidays,
    dureeService: minutesFormatHm(minutes),
    services
  }
}

/**
 * Parse un service pour binder les formulaires
 * @param startAt
 * @param service
 * @returns {{}}
 */
export const parseService = ({ services = [], ...service } = {}) => {
  const { startAt, endAt } = service
  if (startAt) {
    const [startAtDate, startAtTime] = startAt.split('T')
    service.startAtDate = startAtDate
    service.startAtTime = startAtTime.split(':').splice(0, 2).join(':')
  }
  if (endAt) {
    const [, endAtTime] = endAt.split('T')
    service.endAtTime = endAtTime.split(':').splice(0, 2).join(':')
  }
  service.services = services.map(parseService)
  return service
}
